/**
 * Small hand-rolled validation. Deliberately dependency-free: the shapes here
 * are simple and a schema library would be more surface area than value.
 */

export class ValidationError extends Error {
  constructor(
    message: string,
    public field?: string,
  ) {
    super(message)
    this.name = 'ValidationError'
  }
}

export function required<T>(v: T | null | undefined, field: string): T {
  if (v === null || v === undefined || v === '') {
    throw new ValidationError(`${field} is required`, field)
  }
  return v
}

export function str(v: unknown, field: string, opts: { max?: number; min?: number } = {}): string {
  if (typeof v !== 'string') throw new ValidationError(`${field} must be text`, field)
  const s = v.trim()
  if (opts.min !== undefined && s.length < opts.min) {
    throw new ValidationError(`${field} must be at least ${opts.min} characters`, field)
  }
  if (opts.max !== undefined && s.length > opts.max) {
    throw new ValidationError(`${field} must be at most ${opts.max} characters`, field)
  }
  return s
}

export function optStr(v: unknown, field: string, max = 2000): string | null {
  if (v === null || v === undefined || v === '') return null
  return str(v, field, { max })
}

export function num(v: unknown, field: string, opts: { min?: number; max?: number } = {}): number {
  const n = typeof v === 'number' ? v : parseFloat(String(v))
  if (!isFinite(n)) throw new ValidationError(`${field} must be a number`, field)
  if (opts.min !== undefined && n < opts.min) {
    throw new ValidationError(`${field} must be at least ${opts.min}`, field)
  }
  if (opts.max !== undefined && n > opts.max) {
    throw new ValidationError(`${field} must be at most ${opts.max}`, field)
  }
  return n
}

export function bool(v: unknown): boolean {
  return v === true || v === 1 || v === '1' || v === 'true'
}

export function oneOf<T extends string>(v: unknown, allowed: readonly T[], field: string): T {
  const s = String(v)
  if (!allowed.includes(s as T)) {
    throw new ValidationError(`${field} must be one of: ${allowed.join(', ')}`, field)
  }
  return s as T
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export function email(v: unknown, field = 'email'): string {
  const s = str(v, field, { max: 320 }).toLowerCase()
  if (!EMAIL_RE.test(s)) throw new ValidationError(`${field} is not a valid email address`, field)
  return s
}

export function isEmail(v: string): boolean {
  return EMAIL_RE.test(v.trim())
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/

export function dateStr(v: unknown, field: string): string {
  const s = str(v, field, { max: 10 })
  if (!DATE_RE.test(s)) throw new ValidationError(`${field} must be YYYY-MM-DD`, field)
  return s
}

// ------------------------------------------------------------------- GST/PAN

export const STATE_CODES: Record<string, string> = {
  '01': 'Jammu and Kashmir', '02': 'Himachal Pradesh', '03': 'Punjab',
  '04': 'Chandigarh', '05': 'Uttarakhand', '06': 'Haryana', '07': 'Delhi',
  '08': 'Rajasthan', '09': 'Uttar Pradesh', '10': 'Bihar', '11': 'Sikkim',
  '12': 'Arunachal Pradesh', '13': 'Nagaland', '14': 'Manipur', '15': 'Mizoram',
  '16': 'Tripura', '17': 'Meghalaya', '18': 'Assam', '19': 'West Bengal',
  '20': 'Jharkhand', '21': 'Odisha', '22': 'Chhattisgarh', '23': 'Madhya Pradesh',
  '24': 'Gujarat', '26': 'Dadra and Nagar Haveli and Daman and Diu',
  '27': 'Maharashtra', '29': 'Karnataka', '30': 'Goa', '31': 'Lakshadweep',
  '32': 'Kerala', '33': 'Tamil Nadu', '34': 'Puducherry', '35': 'Andaman and Nicobar Islands',
  '36': 'Telangana', '37': 'Andhra Pradesh', '38': 'Ladakh',
  '96': 'Other Country', '97': 'Other Territory',
}

export function stateName(code: string | null | undefined): string {
  if (!code) return ''
  return STATE_CODES[code] ?? ''
}

const GSTIN_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/
const GSTIN_CHARS = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ'

/**
 * Full GSTIN validation including the mod-36 check digit. A typo in a client
 * GSTIN silently breaks their input tax credit, so this is worth doing properly.
 */
export function validateGstin(gstin: string): { valid: boolean; reason?: string } {
  const g = gstin.toUpperCase().trim()
  if (g.length !== 15) return { valid: false, reason: 'GSTIN must be 15 characters' }
  if (!GSTIN_RE.test(g)) return { valid: false, reason: 'GSTIN format is invalid' }
  if (!STATE_CODES[g.slice(0, 2)]) return { valid: false, reason: 'Unknown state code' }

  let sum = 0
  for (let i = 0; i < 14; i++) {
    const val = GSTIN_CHARS.indexOf(g[i])
    const factor = i % 2 === 0 ? 1 : 2
    const product = val * factor
    sum += Math.floor(product / 36) + (product % 36)
  }
  const checksum = GSTIN_CHARS[(36 - (sum % 36)) % 36]
  if (checksum !== g[14]) return { valid: false, reason: 'GSTIN checksum does not match' }
  return { valid: true }
}

export function gstinStateCode(gstin: string): string | null {
  const g = gstin.toUpperCase().trim()
  return g.length >= 2 && STATE_CODES[g.slice(0, 2)] ? g.slice(0, 2) : null
}

export function panFromGstin(gstin: string): string | null {
  const g = gstin.toUpperCase().trim()
  return g.length === 15 ? g.slice(2, 12) : null
}

const PAN_RE = /^[A-Z]{5}[0-9]{4}[A-Z]$/

export function validatePan(pan: string): boolean {
  return PAN_RE.test(pan.toUpperCase().trim())
}

/**
 * CIN: 21 characters, structured as
 *   listing(1) industry(5) state(2) year(4) ownership(3) registration(6)
 * e.g. U74999BR2021OPC052455 — unlisted, Bihar, 2021, One Person Company.
 *
 * Required on invoices and letterheads under section 12(3)(c) of the Companies
 * Act, so a typo here is a compliance defect, not a cosmetic one.
 */
const CIN_RE = /^([LU])(\d{5})([A-Z]{2})(\d{4})([A-Z]{3})(\d{6})$/

const CIN_OWNERSHIP: Record<string, string> = {
  PLC: 'Public Limited Company',
  PTC: 'Private Limited Company',
  OPC: 'One Person Company',
  FTC: 'Subsidiary of a Foreign Company',
  GAP: 'Government of India company',
  SGC: 'State Government company',
  NPL: 'Not-for-Profit company',
  ULL: 'Unlimited Liability company',
  ULT: 'Unlimited Liability company',
  GOI: 'Government of India company',
}

export function validateCin(cin: string): { valid: boolean; reason?: string } {
  const c = cin.toUpperCase().trim()
  if (c.length !== 21) return { valid: false, reason: 'CIN must be exactly 21 characters' }
  const m = c.match(CIN_RE)
  if (!m) return { valid: false, reason: 'CIN format is invalid' }
  if (!CIN_OWNERSHIP[m[5]]) {
    return { valid: false, reason: `"${m[5]}" is not a known company type in a CIN` }
  }
  const year = parseInt(m[4], 10)
  if (year < 1850 || year > 2200) {
    return { valid: false, reason: `Year of incorporation "${m[4]}" looks wrong` }
  }
  return { valid: true }
}

/** Human-readable breakdown, shown as a hint under the CIN field. */
export function describeCin(cin: string): string | null {
  const m = cin.toUpperCase().trim().match(CIN_RE)
  if (!m) return null
  return `${m[1] === 'U' ? 'Unlisted' : 'Listed'} · ${CIN_OWNERSHIP[m[5]] ?? m[5]} · registered in ${m[3]} in ${m[4]}`
}

const IFSC_RE = /^[A-Z]{4}0[A-Z0-9]{6}$/

export function validateIfsc(ifsc: string): boolean {
  return IFSC_RE.test(ifsc.toUpperCase().trim())
}

/** HSN/SAC must be 4, 6 or 8 digits. */
export function validateHsn(code: string): boolean {
  return /^\d{4}(\d{2})?(\d{2})?$/.test(code.trim())
}
